/**
 * Starling brand-polish token set — QAM-side (CookUnity demo, May 2026).
 *
 * Source of truth: ~/Productivity/branding/branding.md (locked Sarah 2026-05-01,
 * brand book absorbed 2026-05-03). Read that doc before adjusting any value
 * here — it carries the precedence rules for marketing-vs-operational surfaces
 * and the dating caveat on the brand book itself.
 *
 * Scope: this module is consumed by the demo-path surfaces — qa-list.tsx and
 * qa-detail-v3.tsx — to apply the Starling brand register on top of the
 * existing V3 working-state tokens already defined in those files. V3 stays;
 * Starling layers on top.
 *
 * Lime `#00E91C` is INTENTIONALLY ABSENT from chrome here. Per Sarah
 * 2026-05-03: "theres so harsh green, we need to make that front and centre"
 * (meaning: be wary of it). Lime appears in exactly two places in the demo
 * path:
 *   1. Inside the Starling logo lockup SVG (where the brand book locks it).
 *   2. As the YOLO bbox label color for the CookUnity bag scene anchor on
 *      the POD photo, where it reads as "model is pointing at this object,"
 *      not as alarm-state.
 *
 * Calm-green tokens (goodCalm / goodCalmBg) are PLACEHOLDER hexes pending
 * Sarah's final pick. TODO Sarah: confirm or replace; see `branding.md` for
 * the open question.
 */

export const Starling = {
  // Typography — the Starling brand pairing (brand book: Poppins headings,
  // Public Sans body fallback). Both faces are loaded in client/index.html.
  // Promoted here (2026-05-22) from per-page FONT_HEAD/FONT_BODY consts so every
  // surface imports one source of truth instead of redefining them locally.
  fontHead: "'Poppins', ui-sans-serif, system-ui, sans-serif",
  fontBody: "'Public Sans', ui-sans-serif, system-ui, sans-serif",

  // Primary brand
  purple: "#592F96",
  purpleSubtleBg: "rgba(89, 47, 150, 0.12)", // for tinted pills (e.g., promoted verified badge)
  purpleSubtleBorder: "rgba(89, 47, 150, 0.18)",
  purpleTintBg: "#F8F5FD", // for purple-tinted card surfaces (e.g., AI conclusion card if/when added)

  // Calm-green semantic — for "matched / cleared / verified" states.
  // Distinct from V3.good / V3.goodBg which are the working "QA decision = Good"
  // colors. Calm-green here is for badges that signal pattern-match or PDR
  // confirmation, not the discrete decision verdict.
  // PLACEHOLDER — Sarah to confirm final hex.
  goodCalm: "#166534",
  goodCalmBg: "#DCFCE7",

  // Light register surfaces (per brand book, light is the canonical Starling
  // register; the prior dark-mode QAM look stays for non-demo ops use).
  pageBg: "#FBFBFB", // brand book "not-quite-white"
  body: "#1B1B1B",   // brand book "not-quite-black"
  bodyMuted: "#6B6B73",
  lightGrey: "#E2E2E2", // borders, rules, footer bg per brand book

  // QAM-specific extension (not in brand book) — soft-blue field tint
  // established for the demo login (Sarah 2026-05-01). Used here for
  // QA Checklist row backgrounds.
  softBlueField: "#F2F4FA",

  // Dark surface for the Verified Reference Photos panel — kept dark to
  // preserve the existing visual separation from the actual POD photo
  // (Sarah 2026-05-03: "we have a different visual background on them today
  // to make sure no one confuses them with the actual submitted POD").
  // Uses brand "not-quite-black" for consistency with the brand palette;
  // existing implementation uses slate `#1E293B` — both are dark, this is a
  // brand-polish swap, not a structural change.
  panelDark: "#1B1B1B",
  panelDarkBorder: "#2A2A30",
  panelDarkText: "#FFFFFF",
  panelDarkMuted: "#888888",

  // Single teal accent for "verified" surfaces (SPEC-qa-detail-v3-refresh §5.6).
  // Replaces the prior blue `#60A5FA` ShieldCheck on the baseline surface and
  // the hardcoded `#5DCAA5` on the Verified Reference Photos panel — one accent,
  // one icon (ShieldCheck), one recipe across every dark "verified" surface.
  verifiedTeal: "#5DCAA5",
  verifiedTealSubtleBg: "rgba(93, 202, 165, 0.16)",
  verifiedTealSubtleBorder: "rgba(93, 202, 165, 0.35)",

  // Lockup source — served by Vite from client/public/branding/, mirrored from
  // ~/Productivity/branding/. Reference via plain <img src={Starling.lockupSrc}>.
  lockupSrc: "/branding/starling-logo-full-colour.svg",
} as const;

export type StarlingTokens = typeof Starling;
